// LiveDemo, non-widget demo CTA (the embedded verification widget was removed).
// Keeps the same visual slot in the CoVerify page but routes users to /contact
// or a guided walkthrough rather than initialising a live verification flow.
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Play, ShieldCheck, ArrowRight, FileText, CalendarClock } from "lucide-react";
import { TID } from "@/constants/coverifyIds";

export default function LiveDemo() {
  return (
    <section id="demo" className="py-20 md:py-28 bg-secondary/40 border-y border-border">
      <div className="container-cv">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="section-eyebrow">See it in action</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.1]">
              Walk through CoVerify{" "}
              <span className="text-accent">with our team.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl">
              Book a 20-minute guided demo and we&apos;ll show you the full verification flow on real-looking sample data, document capture, biometric liveness, AML screening and the audit-ready report, using your own use case.
            </p>

            <ul className="mt-6 space-y-3 text-sm text-primary/80">
              {[
                "Tailored to your accountant / cosec workflow",
                "Sample report + pricing pack sent after the call",
                "No-obligation, under 20 minutes",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-accent" /> {t}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid={TID.demoLaunchBtn} className="h-12 px-7 bg-accent hover:bg-accent/90 text-primary font-semibold text-base shadow-[0_8px_24px_-6px_rgba(124,194,66,0.55)]">
                <Link to="/contact?product=coverify">
                  Book your demo <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12 px-6 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground font-semibold">
                <Link to="/pricing#coverify">See pricing</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative rounded-3xl border border-border bg-white p-6 md:p-8 shadow-[0_30px_60px_-30px_rgba(14,58,95,0.25)]">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-border p-5 bg-secondary/40">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-accent/15 text-primary">
                    <Play className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-display font-bold text-primary">Product walkthrough</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">Live screen-share of the verification dashboard, flows and reports.</p>
                </div>
                <div className="rounded-2xl border border-border p-5 bg-secondary/40">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-accent/15 text-primary">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-display font-bold text-primary">Sample audit report</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">We&apos;ll send a redacted PDF of a real verification report after the call.</p>
                </div>
                <div className="rounded-2xl border border-border p-5 bg-secondary/40">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-accent/15 text-primary">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-display font-bold text-primary">ECCTA mapping</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">How CoVerify maps to the ECCT Act 2023 identity verification requirements.</p>
                </div>
                <div className="rounded-2xl border border-border p-5 bg-secondary/40">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-accent/15 text-primary">
                    <CalendarClock className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-display font-bold text-primary">Fast onboarding</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">Most teams go live within 5 working days, including SSO and data migration.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
